import { Button, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useState } from 'react';
import { Theme } from '../../context/UserContext';
import Icon from '../Icon';
import styles from './Header.module.scss';

const cx = classNames.bind(styles);

export type Page = 'chat' | 'history';
export type ViewType = 'default' | 'expanded';

interface HeaderProps {
  view: ViewType;
  page: Page;
  theme: Theme;
  standalone?: boolean;
  onClose: () => void;
  onChangeView: (view: ViewType) => void;
  onChangePage: (page: Page) => void;
  onCreateNewChat: () => void;
  onChangeTheme: (theme: Theme) => void;
  onOpenStandalone?: () => void;
}

export default function Header({
  view,
  page,
  theme,
  standalone,
  onClose,
  onChangeView,
  onChangePage,
  onCreateNewChat,
  onChangeTheme,
  onOpenStandalone,
}: HeaderProps): React.ReactElement {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const menuWrapperRef = React.useRef<HTMLDivElement>(null);

  const handleToggleMenu = useCallback(() => {
    setMenuIsOpen((prev) => !prev);
  }, []);

  const handleChangePage = useCallback(() => {
    onChangePage(page === 'chat' ? 'history' : 'chat');
    handleToggleMenu();
  }, [page, handleToggleMenu]);

  const handleChangeView = useCallback(() => {
    onChangeView(view === 'default' ? 'expanded' : 'default');
    handleToggleMenu();
  }, [view, handleToggleMenu]);

  const handleChangeTheme = useCallback(() => {
    onChangeTheme(theme === 'light' ? 'dark' : 'light');
    handleToggleMenu();
  }, [theme, handleToggleMenu]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!menuWrapperRef.current?.contains(e.target as Node)) {
        setMenuIsOpen(false);
      }
    };

    document.addEventListener('click', handler);

    return () => {
      document.removeEventListener('click', handler);
    };
  }, []);

  return (
    <div className={cx('header')}>
      <div className={cx('actions')}>
        <div ref={menuWrapperRef} className={cx('menuWrapper')}>
          <Tooltip
            on="hover"
            trigger={
              <Button className={cx('icon')} onClick={handleToggleMenu} data-testid="menu-opener">
                <Icon.Menu />
              </Button>
            }
            content="Menu"
          />
          {menuIsOpen ? (
            <div className={cx('menu')} data-testid="menu">
              <div className={cx('menuList')}>
                <div onClick={handleChangePage} data-testid="menu-item-changePage">
                  <div className={cx('menuListIcon')}>
                    <Icon.History />
                  </div>
                  <div>{page === 'chat' ? 'History' : 'Return to chat'}</div>
                </div>
                {!standalone ? (
                  <div onClick={handleChangeView} data-testid="menu-item-changeView">
                    <div className={cx('menuListIcon')}>
                      {view === 'default' ? (
                        <Icon.ZoomIn className={cx('zoomIcon')} data-testid="icon-zoomIn" />
                      ) : (
                        <Icon.ZoomOut className={cx('zoomIcon')} data-testid="icon-zoomOut" />
                      )}
                    </div>
                    <div>{view === 'default' ? 'Expand window' : 'Minimize window'}</div>
                  </div>
                ) : null}
                <div onClick={handleChangeTheme} data-testid="menu-item-changeTheme">
                  <div className={cx('menuListIcon')}>{theme === 'light' ? <Icon.DarkMode /> : <Icon.LightMode />}</div>
                  <div>Change theme</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <div className={cx('title')}>
        CreditSights <span className={cx('titleLogo')}>Genie</span> <span className={cx('titleBadge')}>beta</span>
      </div>
      <div className={cx('actions')}>
        <Tooltip
          trigger={
            <Button className={cx('icon')} onClick={onCreateNewChat}>
              <Icon.NewChat />
            </Button>
          }
          content="Start new thread"
        />
        {standalone ? null : (
          <>
            <div className={cx('divider')} />
            {onOpenStandalone ? (
              <Tooltip
                trigger={
                  <Button className={cx('icon')} onClick={onOpenStandalone} data-testid="icon-standalone">
                    <Icon.Popout />
                  </Button>
                }
                content="Pop-out to separate window"
              />
            ) : null}
            <Tooltip
              trigger={
                <Button className={cx('icon')} onClick={onClose}>
                  <Icon.Close />
                </Button>
              }
              content="Close"
            />
          </>
        )}
      </div>
    </div>
  );
}
